import { DEFAULT_TAB_COUNT, getDefaultTabTitle } from '../nativeTabsConstants.ts';
import type { FrameAdf, NativeTabsParameters, TabParameters } from '../nativeTabsTypes.ts';

import { generateTabId } from './generateTabId.ts';

/**
 * Returns the initial `tabs` parameter array and matching `frames` ADF nodes
 * for a fresh Native Tabs insert. Each tab gets a compact random id and a
 * default title (`'Tab 1'`, `'Tab 2'`, ...).
 *
 * Pure — no side effects. Called from the manifest's `quickInsert.action`.
 */
export const buildDefaultParameters = (
	count: number = DEFAULT_TAB_COUNT,
): { tabs: TabParameters[]; frames: FrameAdf[] } => {
	const tabs: TabParameters[] = [];
	const frames: FrameAdf[] = [];
	for (let i = 0; i < count; i += 1) {
		tabs.push({ id: generateTabId(), title: getDefaultTabTitle(i) });
		frames.push({ type: 'extensionFrame', content: [{ type: 'paragraph' }] });
	}
	return { tabs, frames };
};

/**
 * Returns a new `tabs` array with one freshly-generated tab appended, plus
 * the new tab itself. Pure — does not mutate the input array.
 *
 * The new tab's title uses `getDefaultTabTitle(currentTabs.length)` so a
 * 2 → 3 grow produces `'Tab 3'`. The id comes from `generateTabId()`.
 */
export const appendTab = (
	currentTabs: ReadonlyArray<TabParameters>,
	options?: { globalColor?: TabParameters['color']; applyToAll?: boolean },
): { tabs: TabParameters[]; newTab: TabParameters } => {
	const newTab: TabParameters = {
		id: generateTabId(),
		title: getDefaultTabTitle(currentTabs.length),
		...(options?.applyToAll && options.globalColor !== undefined
			? { color: options.globalColor }
			: {}),
	};
	return { tabs: [...currentTabs, newTab], newTab };
};

export const meetsTabsIntegrity = (
	parameters: NativeTabsParameters | undefined,
	frameCount: number,
): boolean => (parameters?.tabs ?? []).length === frameCount;

const repairTabsListForFrameCount = (
	currentTabs: ReadonlyArray<TabParameters>,
	frameCount: number,
): TabParameters[] => {
	const tabs = currentTabs.slice(0, frameCount);

	for (let i = tabs.length; i < frameCount; i += 1) {
		tabs.push({ id: generateTabId(), title: getDefaultTabTitle(i) });
	}

	return tabs;
};

export const repairTabsForFrameCount = (
	parameters: NativeTabsParameters | undefined,
	frameCount: number,
): NativeTabsParameters => ({
	tabs: repairTabsListForFrameCount(parameters?.tabs ?? [], frameCount),
	...(parameters?.applyToAll !== undefined ? { applyToAll: parameters.applyToAll } : {}),
});
